import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SettingsIcon, ShieldCheckIcon, DatabaseIcon, BellIcon, KeyIcon, UsersIcon, RefreshCwIcon, CheckCircle2Icon, AlertCircleIcon, ClockIcon, SparklesIcon, SaveIcon, XIcon } from 'lucide-react';
import { useUser } from '../lib/userContext';
import { toast } from 'sonner';
export function Settings() {
  const {
    role,
    hasPermission
  } = useUser();
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<Date>(new Date());
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [syncInterval, setSyncInterval] = useState('hourly');
  const handleFullSync = async () => {
    if (!hasPermission('manage_spaces')) {
      toast.error('Permission Denied', {
        description: 'You do not have permission to sync Knowledge Spaces'
      });
      return;
    }
    setIsSyncing(true);
    toast.info('Starting Full Sync', {
      description: 'Syncing all Knowledge Spaces with Sprinklenet...'
    });
    // Simulate sync
    await new Promise(resolve => setTimeout(resolve, 3000));
    setLastSync(new Date());
    setIsSyncing(false);
    toast.success('Sync Complete', {
      description: 'All Knowledge Spaces are now up to date'
    });
  };
  const handleSaveSettings = () => {
    toast.success('Settings Saved', {
      description: 'Your preferences have been updated'
    });
  };
  return <div className="space-y-8" data-id="element-2347">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden" data-id="element-2348">
        <div className="absolute inset-0 -z-10" data-id="element-2349">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" data-id="element-2350" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" data-id="element-2351" />
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="relative bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-950/95 border border-slate-800/80 rounded-3xl p-10 shadow-2xl shadow-black/40 backdrop-blur-xl" data-id="element-2352">
          <div className="flex items-start justify-between" data-id="element-2353">
            <div data-id="element-2354">
              <h1 className="text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white via-blue-50 to-slate-200 bg-clip-text text-transparent" data-id="element-2355">
                Settings
              </h1>
              <p className="text-slate-400 text-lg flex items-center gap-2" data-id="element-2356">
                <SparklesIcon className="w-5 h-5 text-blue-400" data-id="element-2357" />
                Manage your Quad Atlas preferences and integrations
              </p>
            </div>
            <div className="px-5 py-2.5 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-center gap-2.5 shadow-lg shadow-blue-500/10" data-id="element-2358">
              <ShieldCheckIcon className="w-4 h-4 text-blue-400" data-id="element-2359" />
              <span className="text-sm font-bold text-blue-400" data-id="element-2360">{role}</span>
            </div>
          </div>
        </motion.div>
      </div>
      {/* Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-2361">
        {/* Sync Settings */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl overflow-hidden shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300" data-id="element-2362">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2363" />
          <div className="relative p-8" data-id="element-2364">
            <div className="flex items-center gap-3 mb-6" data-id="element-2365">
              <div className="p-3 rounded-xl bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors" data-id="element-2366">
                <RefreshCwIcon className="w-6 h-6 text-blue-400" data-id="element-2367" />
              </div>
              <div data-id="element-2368">
                <h2 className="text-xl font-bold text-white" data-id="element-2369">
                  Knowledge Space Sync
                </h2>
                <p className="text-sm text-slate-400" data-id="element-2370">
                  Manage Sprinklenet synchronization
                </p>
              </div>
            </div>
            {/* Last Sync Status */}
            <div className="mb-6 p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl" data-id="element-2371">
              <div className="flex items-center justify-between mb-3" data-id="element-2372">
                <span className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2373">
                  Last Sync
                </span>
                <div className="flex items-center gap-2" data-id="element-2374">
                  <CheckCircle2Icon className="w-4 h-4 text-green-400" data-id="element-2375" />
                  <span className="text-xs font-bold text-green-400" data-id="element-2376">
                    Successful
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-white" data-id="element-2377">
                <ClockIcon className="w-4 h-4 text-slate-500" data-id="element-2378" />
                <span className="text-sm font-medium" data-id="element-2379">
                  {lastSync.toLocaleString()}
                </span>
              </div>
            </div>
            {/* Auto Sync Toggle */}
            <div className="mb-6 p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-2380">
              <div className="flex items-center justify-between" data-id="element-2381">
                <div data-id="element-2382">
                  <p className="text-sm font-semibold text-white mb-1" data-id="element-2383">
                    Automatic Sync
                  </p>
                  <p className="text-xs text-slate-400" data-id="element-2384">
                    Sync spaces automatically at set intervals
                  </p>
                </div>
                <button onClick={() => setAutoSyncEnabled(!autoSyncEnabled)} className={`relative w-14 h-7 rounded-full transition-all duration-300 ${autoSyncEnabled ? 'bg-blue-600' : 'bg-slate-700'}`} data-id="element-2385">
                  <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-lg transition-all duration-300 ${autoSyncEnabled ? 'left-8' : 'left-1'}`} data-id="element-2386" />
                </button>
              </div>
            </div>
            {/* Sync Interval */}
            {autoSyncEnabled && <div className="mb-6" data-id="element-2387">
                <label className="block text-sm font-semibold text-slate-400 mb-3 uppercase tracking-wider" data-id="element-2388">
                  Sync Interval
                </label>
                <select value={syncInterval} onChange={e => setSyncInterval(e.target.value)} className="w-full px-4 py-3 bg-slate-950/60 border border-slate-700/60 hover:border-slate-600 focus:border-blue-500/60 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all" data-id="element-2389">
                  <option value="15min" data-id="element-2390">Every 15 minutes</option>
                  <option value="30min" data-id="element-2391">Every 30 minutes</option>
                  <option value="hourly" data-id="element-2392">Every hour</option>
                  <option value="daily" data-id="element-2393">Daily</option>
                </select>
              </div>}
            {/* Manual Sync Button */}
            <button onClick={handleFullSync} disabled={isSyncing || !hasPermission('manage_spaces')} className={`w-full px-6 py-4 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2.5 shadow-xl ${isSyncing || !hasPermission('manage_spaces') ? 'bg-slate-800/60 text-slate-500 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-blue-600/40 hover:shadow-2xl hover:shadow-blue-600/50 hover:scale-[1.02]'}`} data-id="element-2394">
              <RefreshCwIcon className={`w-5 h-5 ${isSyncing ? 'animate-spin' : ''}`} data-id="element-2395" />
              {isSyncing ? 'Syncing...' : 'Run Full Sync Now'}
            </button>
            {!hasPermission('manage_spaces') && <p className="mt-3 text-xs text-amber-400 text-center flex items-center justify-center gap-1.5" data-id="element-2396">
                <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-2397" />
                Requires manage_spaces permission
              </p>}
          </div>
        </motion.div>
        {/* Notification Settings */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl overflow-hidden shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300" data-id="element-2398">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2399" />
          <div className="relative p-8" data-id="element-2400">
            <div className="flex items-center gap-3 mb-6" data-id="element-2401">
              <div className="p-3 rounded-xl bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors" data-id="element-2402">
                <BellIcon className="w-6 h-6 text-purple-400" data-id="element-2403" />
              </div>
              <div data-id="element-2404">
                <h2 className="text-xl font-bold text-white" data-id="element-2405">Notifications</h2>
                <p className="text-sm text-slate-400" data-id="element-2406">
                  Configure alert preferences
                </p>
              </div>
            </div>
            <div className="space-y-4" data-id="element-2407">
              {/* Email Notifications */}
              <div className="p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-2408">
                <div className="flex items-center justify-between" data-id="element-2409">
                  <div data-id="element-2410">
                    <p className="text-sm font-semibold text-white mb-1" data-id="element-2411">
                      Email Notifications
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-2412">
                      Receive updates via email
                    </p>
                  </div>
                  <button onClick={() => setNotificationsEnabled(!notificationsEnabled)} className={`relative w-14 h-7 rounded-full transition-all duration-300 ${notificationsEnabled ? 'bg-purple-600' : 'bg-slate-700'}`} data-id="element-2413">
                    <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-lg transition-all duration-300 ${notificationsEnabled ? 'left-8' : 'left-1'}`} data-id="element-2414" />
                  </button>
                </div>
              </div>
              {/* SLA Alerts */}
              <div className="p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-2415">
                <div className="flex items-center justify-between" data-id="element-2416">
                  <div data-id="element-2417">
                    <p className="text-sm font-semibold text-white mb-1" data-id="element-2418">
                      SLA Alerts
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-2419">
                      Get notified about SLA breaches
                    </p>
                  </div>
                  <button className="relative w-14 h-7 rounded-full transition-all duration-300 bg-purple-600" data-id="element-2420">
                    <div className="absolute top-1 left-8 w-5 h-5 rounded-full bg-white shadow-lg transition-all duration-300" data-id="element-2421" />
                  </button>
                </div>
              </div>
              {/* Account Health */}
              <div className="p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-2422">
                <div className="flex items-center justify-between" data-id="element-2423">
                  <div data-id="element-2424">
                    <p className="text-sm font-semibold text-white mb-1" data-id="element-2425">
                      Account Health Changes
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-2426">
                      Monitor account status updates
                    </p>
                  </div>
                  <button className="relative w-14 h-7 rounded-full transition-all duration-300 bg-purple-600" data-id="element-2427">
                    <div className="absolute top-1 left-8 w-5 h-5 rounded-full bg-white shadow-lg transition-all duration-300" data-id="element-2428" />
                  </button>
                </div>
              </div>
              {/* Sync Status */}
              <div className="p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-2429">
                <div className="flex items-center justify-between" data-id="element-2430">
                  <div data-id="element-2431">
                    <p className="text-sm font-semibold text-white mb-1" data-id="element-2432">
                      Sync Status Updates
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-2433">
                      Knowledge Space sync notifications
                    </p>
                  </div>
                  <button className="relative w-14 h-7 rounded-full transition-all duration-300 bg-slate-700" data-id="element-2434">
                    <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow-lg transition-all duration-300" data-id="element-2435" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        {/* Access Control */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl overflow-hidden shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300" data-id="element-2436">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2437" />
          <div className="relative p-8" data-id="element-2438">
            <div className="flex items-center gap-3 mb-6" data-id="element-2439">
              <div className="p-3 rounded-xl bg-green-500/10 group-hover:bg-green-500/20 transition-colors" data-id="element-2440">
                <UsersIcon className="w-6 h-6 text-green-400" data-id="element-2441" />
              </div>
              <div data-id="element-2442">
                <h2 className="text-xl font-bold text-white" data-id="element-2443">Access Control</h2>
                <p className="text-sm text-slate-400" data-id="element-2444">
                  Manage roles and permissions
                </p>
              </div>
            </div>
            <div className="space-y-4" data-id="element-2445">
              {/* Current Role */}
              <div className="p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl" data-id="element-2446">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-2447">
                  Current Role
                </p>
                <div className="flex items-center gap-3" data-id="element-2448">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg" data-id="element-2449">
                    <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-2450" />
                  </div>
                  <div data-id="element-2451">
                    <p className="text-base font-bold text-white" data-id="element-2452">{role}</p>
                    <p className="text-xs text-slate-400" data-id="element-2453">
                      Active permissions applied
                    </p>
                  </div>
                </div>
              </div>
              {/* Permission Summary */}
              <div className="p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl" data-id="element-2454">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-2455">
                  Your Permissions
                </p>
                <div className="space-y-2.5" data-id="element-2456">
                  <div className="flex items-center gap-2.5" data-id="element-2457">
                    <CheckCircle2Icon className="w-4 h-4 text-green-400 flex-shrink-0" data-id="element-2458" />
                    <span className="text-sm text-slate-300" data-id="element-2459">
                      View client accounts
                    </span>
                  </div>
                  {hasPermission('view_internal_spaces') && <div className="flex items-center gap-2.5" data-id="element-2460">
                      <CheckCircle2Icon className="w-4 h-4 text-green-400 flex-shrink-0" data-id="element-2461" />
                      <span className="text-sm text-slate-300" data-id="element-2462">
                        Access internal Knowledge Spaces
                      </span>
                    </div>}
                  {hasPermission('manage_spaces') && <div className="flex items-center gap-2.5" data-id="element-2463">
                      <CheckCircle2Icon className="w-4 h-4 text-green-400 flex-shrink-0" data-id="element-2464" />
                      <span className="text-sm text-slate-300" data-id="element-2465">
                        Manage Knowledge Spaces
                      </span>
                    </div>}
                  {hasPermission('manage_users') && <div className="flex items-center gap-2.5" data-id="element-2466">
                      <CheckCircle2Icon className="w-4 h-4 text-green-400 flex-shrink-0" data-id="element-2467" />
                      <span className="text-sm text-slate-300" data-id="element-2468">
                        Manage team members
                      </span>
                    </div>}
                  {!hasPermission('manage_spaces') && <div className="flex items-center gap-2.5" data-id="element-2469">
                      <XIcon className="w-4 h-4 text-slate-600 flex-shrink-0" data-id="element-2470" />
                      <span className="text-sm text-slate-500" data-id="element-2471">
                        Manage Knowledge Spaces
                      </span>
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        {/* API & Integrations */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl overflow-hidden shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300" data-id="element-2472">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2473" />
          <div className="relative p-8" data-id="element-2474">
            <div className="flex items-center gap-3 mb-6" data-id="element-2475">
              <div className="p-3 rounded-xl bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors" data-id="element-2476">
                <KeyIcon className="w-6 h-6 text-amber-400" data-id="element-2477" />
              </div>
              <div data-id="element-2478">
                <h2 className="text-xl font-bold text-white" data-id="element-2479">
                  API & Integrations
                </h2>
                <p className="text-sm text-slate-400" data-id="element-2480">
                  Connect external services
                </p>
              </div>
            </div>
            <div className="space-y-4" data-id="element-2481">
              {/* Sprinklenet Connection */}
              <div className="p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl" data-id="element-2482">
                <div className="flex items-center justify-between mb-3" data-id="element-2483">
                  <div className="flex items-center gap-3" data-id="element-2484">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg" data-id="element-2485">
                      <DatabaseIcon className="w-5 h-5 text-white" data-id="element-2486" />
                    </div>
                    <div data-id="element-2487">
                      <p className="text-sm font-semibold text-white" data-id="element-2488">
                        Sprinklenet
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-2489">
                        Knowledge Spaces Platform
                      </p>
                    </div>
                  </div>
                  <div className="px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-1.5" data-id="element-2490">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-2491" />
                    <span className="text-xs font-bold text-green-400" data-id="element-2492">
                      Connected
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed" data-id="element-2493">
                  All Knowledge Spaces are synced with your Sprinklenet account
                </p>
              </div>
              {/* API Key Management */}
              <div className="p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all cursor-pointer" data-id="element-2494">
                <div className="flex items-center justify-between" data-id="element-2495">
                  <div data-id="element-2496">
                    <p className="text-sm font-semibold text-white mb-1" data-id="element-2497">
                      API Keys
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-2498">
                      Manage authentication tokens
                    </p>
                  </div>
                  <button className="px-4 py-2 bg-slate-800/60 hover:bg-slate-700/60 text-white text-xs font-medium rounded-lg transition-all hover:scale-105" data-id="element-2499">
                    Manage
                  </button>
                </div>
              </div>
              {/* Webhooks */}
              <div className="p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all cursor-pointer" data-id="element-2500">
                <div className="flex items-center justify-between" data-id="element-2501">
                  <div data-id="element-2502">
                    <p className="text-sm font-semibold text-white mb-1" data-id="element-2503">
                      Webhooks
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-2504">
                      Configure event notifications
                    </p>
                  </div>
                  <button className="px-4 py-2 bg-slate-800/60 hover:bg-slate-700/60 text-white text-xs font-medium rounded-lg transition-all hover:scale-105" data-id="element-2505">
                    Configure
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      {/* Save Button */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="flex items-center justify-end gap-4" data-id="element-2506">
        <button className="px-8 py-4 bg-slate-800/60 hover:bg-slate-700/80 border border-slate-700/60 hover:border-slate-600 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-xl shadow-black/20 hover:shadow-2xl hover:shadow-black/30 hover:scale-105" data-id="element-2507">
          Reset to Defaults
        </button>
        <button onClick={handleSaveSettings} className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold rounded-xl transition-all duration-200 shadow-xl shadow-blue-600/40 hover:shadow-2xl hover:shadow-blue-600/50 flex items-center gap-2.5 hover:scale-105" data-id="element-2508">
          <SaveIcon className="w-5 h-5" data-id="element-2509" />
          Save Settings
        </button>
      </motion.div>
    </div>;
}